import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  Building2, Plus, Edit, Trash2, Search, Mail, Phone, Globe, AlertTriangle, 
  CheckCircle, XCircle, RefreshCw, ExternalLink, Shield, AlertOctagon, MapPin,
  Eye, Upload, Download, Send, FileText, Users, Sparkles, Filter, MailPlus, 
  UserPlus, Briefcase, Target, Check, Calendar, Clock
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const RISK_LEVELS = {
  verified: { label: 'Verified', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
  unverified: { label: 'Unverified', color: 'bg-slate-100 text-slate-700', icon: Shield },
  suspicious: { label: 'Suspicious', color: 'bg-amber-100 text-amber-700', icon: AlertTriangle },
  fake: { label: 'Fake/Shell', color: 'bg-rose-100 text-rose-700', icon: AlertOctagon }
};

const STATUS_OPTIONS = [
  { value: 'prospect', label: 'Prospect', color: 'bg-blue-100 text-blue-700' },
  { value: 'contacted', label: 'Contacted', color: 'bg-amber-100 text-amber-700' },
  { value: 'client', label: 'Client', color: 'bg-emerald-100 text-emerald-700' },
  { value: 'customer', label: 'Customer', color: 'bg-purple-100 text-purple-700' },
  { value: 'not_interested', label: 'Not Interested', color: 'bg-slate-100 text-slate-700' }
];

const DATE_FILTER_OPTIONS = [
  { value: 'all', label: 'All Time' },
  { value: 'today', label: 'Today' },
  { value: 'week', label: 'This Week' },
  { value: 'month', label: 'This Month' },
  { value: 'custom', label: 'Custom Range' }
];

const MANDATORY_DOCS = [
  { id: 'relieving_letter', label: 'Relieving Letter' },
  { id: 'experience_letter', label: 'Experience Letter' },
  { id: 'service_letter', label: 'Service Letter' },
  { id: 'declaration_form', label: 'Declaration Form' },
  { id: 'salary_slip', label: 'Salary Slip' },
  { id: 'appointment_letter', label: 'Appointment Letter' },
  { id: 'others', label: 'Others' }
];

export default function CompaniesTab() {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [showResearchDialog, setShowResearchDialog] = useState(false);
  const [showBulkImportDialog, setShowBulkImportDialog] = useState(false);
  const [showBulkEmailDialog, setShowBulkEmailDialog] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [viewingCompany, setViewingCompany] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [webSearchQuery, setWebSearchQuery] = useState('');
  const [webSearchResults, setWebSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [aiResearching, setAiResearching] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [contactedFilter, setContactedFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  
  // Selection state
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  
  // Bulk email state
  const [proposalTemplates, setProposalTemplates] = useState([]);
  const [emailForm, setEmailForm] = useState({
    subject: 'JustVerified - Background Verification Services',
    body: '',
    attachBrochure: true,
    attachPricing: true,
    selectedTemplate: ''
  });
  const [sendingEmail, setSendingEmail] = useState(false);
  
  // Bulk import state
  const [importData, setImportData] = useState('');
  const [importing, setImporting] = useState(false);
  
  const [formData, setFormData] = useState({
    company_name: '',
    industry: '',
    company_type: 'private',
    hr_email: '',
    hr_phone: '',
    hr_name: '',
    website: '',
    address: '',
    cin_number: '',
    gst_number: '',
    verification_contact: '',
    verification_email_template: '',
    special_instructions: '',
    is_special: false,
    risk_level: 'unverified',
    negative_info: '',
    last_verified_date: '',
    linkedin_url: '',
    employee_count: '',
    founded_year: '',
    // New fields
    status: 'prospect',
    contact_person: '',
    contact_person_email: '',
    contact_person_phone: '',
    contact_person_designation: '',
    brief: '',
    last_contacted_date: '',
    contact_history: [],
    mandatory_docs: [],
    created_at: '',
    updated_at: ''
  });

  useEffect(() => {
    fetchCompanies();
    fetchProposalTemplates();
  }, []);

  const fetchCompanies = async () => {
    try {
      const response = await axios.get('/company-templates');
      setCompanies(response.data || []);
    } catch (error) {
      console.error('Failed to fetch companies:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProposalTemplates = async () => {
    try {
      const response = await axios.get('/proposal-templates');
      setProposalTemplates(response.data || []);
    } catch (error) {
      console.error('Failed to fetch proposal templates:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      company_name: '',
      industry: '',
      company_type: 'private',
      hr_email: '',
      hr_phone: '',
      hr_name: '',
      website: '',
      address: '',
      cin_number: '',
      gst_number: '',
      verification_contact: '',
      verification_email_template: '',
      special_instructions: '',
      is_special: false,
      risk_level: 'unverified',
      negative_info: '',
      last_verified_date: '',
      linkedin_url: '',
      employee_count: '',
      founded_year: '',
      status: 'prospect',
      contact_person: '',
      contact_person_email: '',
      contact_person_phone: '',
      contact_person_designation: '',
      brief: '',
      last_contacted_date: '',
      contact_history: [],
      mandatory_docs: []
    });
    setEditingCompany(null);
  };

  const openCreateDialog = () => {
    resetForm();
    setShowDialog(true);
  };

  const openEditDialog = (company) => {
    setEditingCompany(company);
    setFormData({
      company_name: company.company_name || '',
      industry: company.industry || '',
      company_type: company.company_type || 'private',
      hr_email: company.hr_email || '',
      hr_phone: company.hr_phone || '',
      hr_name: company.hr_name || '',
      website: company.website || '',
      address: company.address || '',
      cin_number: company.cin_number || '',
      gst_number: company.gst_number || '',
      verification_contact: company.verification_contact || '',
      verification_email_template: company.verification_email_template || '',
      special_instructions: company.special_instructions || '',
      is_special: company.is_special || false,
      risk_level: company.risk_level || 'unverified',
      negative_info: company.negative_info || '',
      last_verified_date: company.last_verified_date || '',
      linkedin_url: company.linkedin_url || '',
      employee_count: company.employee_count || '',
      founded_year: company.founded_year || '',
      status: company.status || 'prospect',
      contact_person: company.contact_person || '',
      contact_person_email: company.contact_person_email || '',
      contact_person_phone: company.contact_person_phone || '',
      contact_person_designation: company.contact_person_designation || '',
      brief: company.brief || '',
      last_contacted_date: company.last_contacted_date || '',
      contact_history: company.contact_history || [],
      mandatory_docs: company.mandatory_docs || []
    });
    setShowDialog(true);
  };

  const openViewDialog = (company) => {
    setViewingCompany(company);
    setShowViewDialog(true);
  };

  // Date filter helper
  const isWithinDateRange = (dateStr) => {
    if (!dateStr) return dateFilter === 'all';
    
    const date = new Date(dateStr);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    switch (dateFilter) {
      case 'today':
        const todayEnd = new Date(today);
        todayEnd.setHours(23, 59, 59, 999);
        return date >= today && date <= todayEnd;
      case 'week':
        const weekStart = new Date(today);
        weekStart.setDate(today.getDate() - today.getDay());
        return date >= weekStart;
      case 'month':
        const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
        return date >= monthStart;
      case 'custom':
        const from = dateFrom ? new Date(dateFrom) : null;
        const to = dateTo ? new Date(dateTo) : null;
        if (from) from.setHours(0, 0, 0, 0);
        if (to) to.setHours(23, 59, 59, 999);
        if (from && to) return date >= from && date <= to;
        if (from) return date >= from;
        if (to) return date <= to;
        return true;
      default:
        return true;
    }
  };

  const toggleMandatoryDoc = (docId) => {
    setFormData(prev => {
      const docs = prev.mandatory_docs || [];
      if (docs.includes(docId)) {
        return { ...prev, mandatory_docs: docs.filter(d => d !== docId) };
      } else {
        return { ...prev, mandatory_docs: [...docs, docId] };
      }
    });
  };

  const handleSubmit = async () => {
    if (!formData.company_name) {
      toast.error('Company name is required');
      return;
    }

    try {
      const payload = {
        ...formData,
        last_verified_date: formData.last_verified_date || new Date().toISOString().split('T')[0]
      };
      
      if (editingCompany) {
        await axios.put(`/company-templates/${editingCompany.id}`, payload);
        toast.success('Company updated successfully');
      } else {
        await axios.post('/company-templates', payload);
        toast.success('Company added successfully');
      }
      setShowDialog(false);
      resetForm();
      fetchCompanies();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save company');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this company?')) return;
    
    try {
      await axios.delete(`/company-templates/${id}`);
      toast.success('Company deleted');
      fetchCompanies();
    } catch (error) {
      toast.error('Failed to delete company');
    }
  };

  // AI Research using Gemini
  const handleAIResearch = async (companyName) => {
    setAiResearching(true);
    try {
      const response = await axios.post('/research/company', { query: companyName });
      if (response.data?.results?.length > 0) {
        setWebSearchResults(response.data.results);
        toast.success('AI research completed!');
      } else {
        toast.info('No results found. Try a different search term.');
      }
    } catch (error) {
      toast.error('AI research failed. Please try again.');
    } finally {
      setAiResearching(false);
    }
  };

  // Web Research Function
  const handleWebResearch = async () => {
    if (!webSearchQuery.trim()) {
      toast.error('Please enter a search query');
      return;
    }
    
    setSearching(true);
    try {
      const response = await axios.post('/research/company', { query: webSearchQuery });
      setWebSearchResults(response.data?.results || []);
      if (response.data?.results?.length === 0) {
        toast.info('No results found. Try a different search term.');
      }
    } catch (error) {
      toast.error('Research failed');
    } finally {
      setSearching(false);
    }
  };

  const importFromResearch = (result) => {
    setFormData({
      ...formData,
      company_name: result.company_name || '',
      website: result.website || '',
      industry: result.industry || '',
      address: result.address || '',
      hr_email: result.hr_email || '',
      hr_phone: result.hr_phone || '',
      linkedin_url: result.linkedin_url || '',
      risk_level: result.risk_level || 'unverified',
      brief: `Imported from web research on ${new Date().toLocaleDateString()}`
    });
    setShowResearchDialog(false);
    setShowDialog(true);
    toast.success('Data imported! Please review and save.');
  };

  // Selection handlers
  const handleSelectAll = (checked) => {
    setSelectAll(checked);
    if (checked) {
      setSelectedCompanies(filteredCompanies.map(c => c.id));
    } else {
      setSelectedCompanies([]);
    }
  };

  const handleSelectCompany = (companyId, checked) => {
    if (checked) {
      setSelectedCompanies([...selectedCompanies, companyId]);
    } else {
      setSelectedCompanies(selectedCompanies.filter(id => id !== companyId));
      setSelectAll(false);
    }
  };

  // Bulk Import
  const handleBulkImport = async () => {
    if (!importData.trim()) {
      toast.error('Please paste CSV data');
      return;
    }
    
    setImporting(true);
    try {
      const lines = importData.trim().split('\n');
      const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
      
      let imported = 0;
      for (let i = 1; i < lines.length; i++) {
        const values = lines[i].split(',').map(v => v.trim());
        const company = {};
        headers.forEach((header, idx) => {
          company[header.replace(/\s+/g, '_')] = values[idx] || '';
        });
        
        if (company.company_name) {
          await axios.post('/company-templates', {
            company_name: company.company_name,
            industry: company.industry || '',
            hr_email: company.hr_email || company.email || '',
            hr_phone: company.hr_phone || company.phone || '',
            website: company.website || '',
            status: company.status || 'prospect',
            contact_person: company.contact_person || '',
            brief: company.brief || company.description || '',
            risk_level: 'unverified'
          });
          imported++;
        }
      }
      
      toast.success(`Imported ${imported} companies successfully`);
      setShowBulkImportDialog(false);
      setImportData('');
      fetchCompanies();
    } catch (error) {
      toast.error('Import failed: ' + (error.message || 'Unknown error'));
    } finally {
      setImporting(false);
    }
  };

  // Bulk Email
  const handleBulkEmail = async () => {
    if (selectedCompanies.length === 0) {
      toast.error('Please select companies to email');
      return;
    }
    
    setSendingEmail(true);
    try {
      const selectedData = companies.filter(c => selectedCompanies.includes(c.id));
      
      for (const company of selectedData) {
        const emailTo = company.contact_person_email || company.hr_email;
        if (!emailTo) continue;
        
        // Send email via backend
        await axios.post('/send-email', {
          to_email: emailTo,
          subject: emailForm.subject,
          body: emailForm.body,
          attachments: {
            brochure: emailForm.attachBrochure,
            pricing: emailForm.attachPricing,
            proposal_template_id: emailForm.selectedTemplate
          }
        });
        
        // Mark as contacted
        await axios.put(`/company-templates/${company.id}`, {
          ...company,
          status: company.status === 'prospect' ? 'contacted' : company.status,
          last_contacted_date: new Date().toISOString().split('T')[0],
          contact_history: [
            ...(company.contact_history || []),
            {
              date: new Date().toISOString(),
              type: 'email',
              subject: emailForm.subject,
              sent_by: 'admin'
            }
          ]
        });
      }
      
      toast.success(`Emails sent to ${selectedData.filter(c => c.contact_person_email || c.hr_email).length} companies`);
      setShowBulkEmailDialog(false);
      setSelectedCompanies([]);
      setSelectAll(false);
      fetchCompanies();
    } catch (error) {
      toast.error('Failed to send some emails');
    } finally {
      setSendingEmail(false);
    }
  };

  // Download CSV Template
  const downloadTemplate = () => {
    const template = 'company_name,industry,hr_email,hr_phone,website,contact_person,brief,status\nExample Corp,Technology,hr@example.com,+91-9876543210,https://example.com,John Doe,Brief description,prospect';
    const blob = new Blob([template], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'company_import_template.csv';
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Template downloaded');
  };

  // Export Companies (Feb 2026 — added `added_date` column, use hr_name as
  // fallback for contact_person so bulk-imported / auto-added rows aren't
  // blank in the export.)
  const exportCompanies = () => {
    const dataToExport = selectedCompanies.length > 0 
      ? companies.filter(c => selectedCompanies.includes(c.id))
      : filteredCompanies;
    
    const headers = ['company_name', 'industry', 'hr_email', 'hr_phone', 'website', 'contact_person', 'status', 'brief', 'added_date', 'updated_date'];
    const fmt = (iso) => {
      if (!iso) return '';
      try {
        const d = new Date(iso);
        if (isNaN(d.getTime())) return '';
        return d.toISOString().split('T')[0];
      } catch { return ''; }
    };
    const rowFor = (c) => ({
      company_name: c.company_name || c.name || '',
      industry: c.industry || '',
      hr_email: c.hr_email || c.verification_email || c.contact_email || '',
      hr_phone: c.hr_phone || c.phone || c.hr_contact_phone || '',
      website: c.website || '',
      contact_person: c.contact_person || c.hr_name || c.hr_contact_name || '',
      status: c.status || '',
      brief: c.brief || '',
      added_date: fmt(c.created_at),
      updated_date: fmt(c.updated_at || c.created_at),
    });
    const csv = [
      headers.join(','),
      ...dataToExport.map(c => {
        const row = rowFor(c);
        return headers.map(h => `"${String(row[h] || '').replace(/"/g, '""')}"`).join(',');
      })
    ].join('\n');
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'companies_export.csv';
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${dataToExport.length} companies`);
  };

  const filteredCompanies = companies.filter(comp => {
    const matchesSearch = comp.company_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      comp.industry?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      comp.hr_email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      comp.contact_person?.toLowerCase().includes(searchQuery.toLowerCase());
    
    // Risk level filter
    let matchesRisk = true;
    if (activeTab === 'verified') matchesRisk = comp.risk_level === 'verified';
    else if (activeTab === 'suspicious') matchesRisk = comp.risk_level === 'suspicious' || comp.risk_level === 'fake';
    else if (activeTab === 'unverified') matchesRisk = comp.risk_level === 'unverified';
    
    // Status filter
    const matchesStatus = statusFilter === 'all' || comp.status === statusFilter;
    
    // Contacted filter
    let matchesContacted = true;
    if (contactedFilter === 'contacted') matchesContacted = !!comp.last_contacted_date;
    else if (contactedFilter === 'not_contacted') matchesContacted = !comp.last_contacted_date;
    
    // Date filter (on created_at or updated_at)
    const matchesDate = isWithinDateRange(comp.updated_at || comp.created_at);
    
    return matchesSearch && matchesRisk && matchesStatus && matchesContacted && matchesDate;
  });

  const getRiskBadge = (risk_level) => {
    const risk = RISK_LEVELS[risk_level] || RISK_LEVELS.unverified;
    const Icon = risk.icon;
    return (
      <Badge className={`${risk.color} flex items-center gap-1`}>
        <Icon className="w-3 h-3" />{risk.label}
      </Badge>
    );
  };

  const getStatusBadge = (status) => {
    const statusOption = STATUS_OPTIONS.find(s => s.value === status) || STATUS_OPTIONS[0];
    return <Badge className={statusOption.color}>{statusOption.label}</Badge>;
  };

  if (loading) return <div className="p-8 text-center">Loading...</div>;

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Company Database</h2>
          <p className="text-slate-600">Employer contacts for employment verification - Auto-initiate when HR email matches</p>
        </div>
        <Badge className="bg-indigo-100 text-indigo-700 text-lg px-4 py-2">
          {companies.length} Companies
        </Badge>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-wrap gap-2 mb-6">
        <Button variant="outline" onClick={() => setShowResearchDialog(true)} className="border-blue-300 text-blue-700 hover:bg-blue-50">
          <Sparkles className="w-4 h-4 mr-2" />AI Research
        </Button>
        <Button variant="outline" onClick={() => setShowBulkImportDialog(true)}>
          <Upload className="w-4 h-4 mr-2" />Bulk Import
        </Button>
        <Button variant="outline" onClick={exportCompanies}>
          <Download className="w-4 h-4 mr-2" />Export
        </Button>
        <Button 
          variant="outline" 
          onClick={() => setShowBulkEmailDialog(true)}
          disabled={selectedCompanies.length === 0}
          className={selectedCompanies.length > 0 ? 'border-emerald-300 text-emerald-700' : ''}
        >
          <MailPlus className="w-4 h-4 mr-2" />
          Bulk Email {selectedCompanies.length > 0 && `(${selectedCompanies.length})`}
        </Button>
        <Button onClick={openCreateDialog} className="bg-indigo-600 hover:bg-indigo-700 ml-auto">
          <Plus className="w-4 h-4 mr-2" />Add Company
        </Button>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="pt-4">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                <Input
                  placeholder="Search companies..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                {STATUS_OPTIONS.map(s => (
                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={contactedFilter} onValueChange={setContactedFilter}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Contacted" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="contacted">Contacted</SelectItem>
                <SelectItem value="not_contacted">Not Contacted</SelectItem>
              </SelectContent>
            </Select>
            <Select value={dateFilter} onValueChange={setDateFilter}>
              <SelectTrigger className="w-[140px]">
                <Calendar className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Date" />
              </SelectTrigger>
              <SelectContent>
                {DATE_FILTER_OPTIONS.map(d => (
                  <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {dateFilter === 'custom' && (
              <>
                <Input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="w-[140px]"
                  placeholder="From"
                />
                <Input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="w-[140px]"
                  placeholder="To"
                />
              </>
            )}
            <Button variant="ghost" size="sm" onClick={() => { setSearchQuery(''); setStatusFilter('all'); setContactedFilter('all'); setDateFilter('all'); setDateFrom(''); setDateTo(''); setActiveTab('all'); }}>
              <XCircle className="w-4 h-4 mr-1" />Clear
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Risk Level Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-4">
        <TabsList>
          <TabsTrigger value="all">All ({companies.length})</TabsTrigger>
          <TabsTrigger value="verified">Verified ({companies.filter(c => c.risk_level === 'verified').length})</TabsTrigger>
          <TabsTrigger value="unverified">Unverified ({companies.filter(c => c.risk_level === 'unverified').length})</TabsTrigger>
          <TabsTrigger value="suspicious">Suspicious ({companies.filter(c => c.risk_level === 'suspicious' || c.risk_level === 'fake').length})</TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Companies Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="w-5 h-5 text-indigo-600" />
            Company Records
          </CardTitle>
        </CardHeader>
        <CardContent>
          {filteredCompanies.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <Building2 className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <p>No companies found. Use AI Research or Add Company to get started.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[40px]">
                    <Checkbox
                      checked={selectAll}
                      onCheckedChange={handleSelectAll}
                    />
                  </TableHead>
                  <TableHead>Company Name</TableHead>
                  <TableHead>Contact Person</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Mandatory Docs</TableHead>
                  <TableHead>Risk</TableHead>
                  <TableHead>Updated</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredCompanies.map((comp) => (
                  <TableRow key={comp.id} className={comp.risk_level === 'fake' ? 'bg-rose-50' : comp.risk_level === 'suspicious' ? 'bg-amber-50' : ''}>
                    <TableCell>
                      <Checkbox
                        checked={selectedCompanies.includes(comp.id)}
                        onCheckedChange={(checked) => handleSelectCompany(comp.id, checked)}
                      />
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-slate-400" />
                        <div>
                          <div className="font-medium">{comp.company_name}</div>
                          {comp.industry && <Badge variant="outline" className="text-xs mt-1">{comp.industry}</Badge>}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      {comp.contact_person && (
                        <div>
                          <div className="font-medium text-sm">{comp.contact_person}</div>
                          {comp.contact_person_designation && <div className="text-xs text-slate-500">{comp.contact_person_designation}</div>}
                          {comp.contact_person_email && <div className="text-xs text-blue-600">{comp.contact_person_email}</div>}
                        </div>
                      )}
                      {!comp.contact_person && comp.hr_name && (
                        <div className="text-sm">{comp.hr_name}</div>
                      )}
                    </TableCell>
                    <TableCell>{getStatusBadge(comp.status)}</TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1 max-w-[150px]">
                        {comp.mandatory_docs?.slice(0, 2).map(doc => (
                          <Badge key={doc} variant="outline" className="text-xs">
                            {MANDATORY_DOCS.find(d => d.id === doc)?.label.split(' ')[0] || doc}
                          </Badge>
                        ))}
                        {comp.mandatory_docs?.length > 2 && (
                          <Badge variant="outline" className="text-xs">+{comp.mandatory_docs.length - 2}</Badge>
                        )}
                        {(!comp.mandatory_docs || comp.mandatory_docs.length === 0) && (
                          <span className="text-xs text-slate-400">-</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <p className="text-sm text-slate-600 max-w-[200px] truncate" title={comp.brief}>
                        {comp.brief || '-'}
                      </p>
                    </TableCell>
                    <TableCell>{getRiskBadge(comp.risk_level)}</TableCell>
                    <TableCell>
                      <div className="text-sm">
                        {comp.updated_at || comp.created_at ? (
                          <div className="flex items-center gap-1 text-slate-600">
                            <Clock className="w-3 h-3" />
                            {new Date(comp.updated_at || comp.created_at).toLocaleDateString()}
                          </div>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="sm" onClick={() => openViewDialog(comp)} title="View Details">
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => openEditDialog(comp)} title="Edit">
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" className="text-rose-600" onClick={() => handleDelete(comp.id)} title="Delete">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* View Dialog */}
      <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5" />
              {viewingCompany?.company_name}
            </DialogTitle>
          </DialogHeader>
          {viewingCompany && (
            <div className="space-y-6 py-4">
              <div className="flex gap-2 flex-wrap">
                {getRiskBadge(viewingCompany.risk_level)}
                {getStatusBadge(viewingCompany.status)}
                {viewingCompany.is_special && <Badge className="bg-amber-100 text-amber-700">Special</Badge>}
                {viewingCompany.auto_initiate && <Badge className="bg-green-100 text-green-700">Auto-Initiate</Badge>}
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <h4 className="font-semibold text-slate-700">Company Details</h4>
                  <div className="bg-slate-50 p-4 rounded-lg space-y-2">
                    {viewingCompany.industry && <p><strong>Industry:</strong> {viewingCompany.industry}</p>}
                    {viewingCompany.company_type && <p><strong>Type:</strong> {viewingCompany.company_type}</p>}
                    {(viewingCompany.website || viewingCompany.website_url) && (
                      <p className="flex items-center gap-2">
                        <Globe className="w-4 h-4 text-slate-500" />
                        <strong>Website:</strong> 
                        <a href={viewingCompany.website || viewingCompany.website_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                          {viewingCompany.website || viewingCompany.website_url}
                        </a>
                      </p>
                    )}
                    {viewingCompany.address && <p><strong>Address:</strong> {viewingCompany.address}</p>}
                    {(viewingCompany.cin_number || viewingCompany.mca_cin) && <p><strong>CIN:</strong> {viewingCompany.cin_number || viewingCompany.mca_cin}</p>}
                    {viewingCompany.gst_number && <p><strong>GST:</strong> {viewingCompany.gst_number}</p>}
                    {viewingCompany.employee_count && <p><strong>Employees:</strong> {viewingCompany.employee_count}</p>}
                  </div>
                </div>
                
                <div className="space-y-2">
                  <h4 className="font-semibold text-slate-700">HR / Verification Contact</h4>
                  <div className="bg-blue-50 p-4 rounded-lg space-y-2">
                    {viewingCompany.hr_name && (
                      <p className="flex items-center gap-2">
                        <Users className="w-4 h-4 text-blue-500" />
                        <strong>Name:</strong> {viewingCompany.hr_name}
                      </p>
                    )}
                    {viewingCompany.hr_email && (
                      <p className="flex items-center gap-2">
                        <Mail className="w-4 h-4 text-blue-500" />
                        <strong>Email:</strong> 
                        <a href={`mailto:${viewingCompany.hr_email}`} className="text-blue-600 hover:underline">{viewingCompany.hr_email}</a>
                      </p>
                    )}
                    {(viewingCompany.hr_phone || viewingCompany.contact_phone) && (
                      <p className="flex items-center gap-2">
                        <Phone className="w-4 h-4 text-blue-500" />
                        <strong>Phone:</strong> {viewingCompany.hr_phone || viewingCompany.contact_phone}
                      </p>
                    )}
                    {viewingCompany.verification_process && (
                      <p><strong>Verification Process:</strong> {viewingCompany.verification_process}</p>
                    )}
                    {viewingCompany.verification_portal_url && (
                      <p className="flex items-center gap-2">
                        <ExternalLink className="w-4 h-4 text-blue-500" />
                        <strong>Portal:</strong>
                        <a href={viewingCompany.verification_portal_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                          {viewingCompany.verification_portal_url}
                        </a>
                      </p>
                    )}
                    {!viewingCompany.hr_email && !viewingCompany.hr_name && (
                      <p className="text-slate-500">No HR contact assigned</p>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Contact Person (Sales/CRM) */}
              {(viewingCompany.contact_person || viewingCompany.contact_person_email) && (
                <div className="space-y-2">
                  <h4 className="font-semibold text-slate-700">Sales Contact Person</h4>
                  <div className="bg-slate-50 p-4 rounded-lg space-y-2">
                    <div className="grid grid-cols-2 gap-4">
                      {viewingCompany.contact_person && <p><strong>Name:</strong> {viewingCompany.contact_person}</p>}
                      {viewingCompany.contact_person_designation && <p><strong>Designation:</strong> {viewingCompany.contact_person_designation}</p>}
                      {viewingCompany.contact_person_email && (
                        <p className="flex items-center gap-2">
                          <Mail className="w-4 h-4 text-slate-500" />
                          <strong>Email:</strong> 
                          <a href={`mailto:${viewingCompany.contact_person_email}`} className="text-blue-600 hover:underline">{viewingCompany.contact_person_email}</a>
                        </p>
                      )}
                      {viewingCompany.contact_person_phone && (
                        <p className="flex items-center gap-2">
                          <Phone className="w-4 h-4 text-slate-500" />
                          <strong>Phone:</strong> {viewingCompany.contact_person_phone}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}
              
              {/* Mandatory Documents */}
              {viewingCompany.mandatory_docs?.length > 0 && (
                <div className="space-y-2">
                  <h4 className="font-semibold text-slate-700">Mandatory Documents</h4>
                  <div className="bg-slate-50 p-4 rounded-lg">
                    <div className="flex flex-wrap gap-2">
                      {viewingCompany.mandatory_docs.map(docId => {
                        const doc = MANDATORY_DOCS.find(d => d.id === docId);
                        return doc ? (
                          <Badge key={docId} variant="outline" className="bg-blue-50">
                            <FileText className="w-3 h-3 mr-1" />{doc.label}
                          </Badge>
                        ) : null;
                      })}
                    </div>
                  </div>
                </div>
              )}
              
              {viewingCompany.brief && (
                <div className="space-y-2">
                  <h4 className="font-semibold text-slate-700">Brief / Notes</h4>
                  <div className="bg-slate-50 p-4 rounded-lg">
                    <p>{viewingCompany.brief}</p>
                  </div>
                </div>
              )}
              
              {viewingCompany.contact_history?.length > 0 && (
                <div className="space-y-2">
                  <h4 className="font-semibold text-slate-700">Contact History</h4>
                  <div className="bg-slate-50 p-4 rounded-lg space-y-2">
                    {viewingCompany.contact_history.map((h, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm">
                        <Badge variant="outline">{h.type}</Badge>
                        <span>{new Date(h.date).toLocaleDateString()}</span>
                        <span className="text-slate-500">{h.subject}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowViewDialog(false)}>Close</Button>
            <Button onClick={() => { setShowViewDialog(false); openEditDialog(viewingCompany); }}>
              <Edit className="w-4 h-4 mr-2" />Edit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AI Research Dialog */}
      <Dialog open={showResearchDialog} onOpenChange={setShowResearchDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-indigo-600" />
              AI Research - Find Company Data
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="flex gap-2">
              <Input
                value={webSearchQuery}
                onChange={(e) => setWebSearchQuery(e.target.value)}
                placeholder="Enter company name to research..."
                onKeyDown={(e) => e.key === 'Enter' && handleWebResearch()}
              />
              <Button onClick={handleWebResearch} disabled={searching} className="bg-indigo-600 hover:bg-indigo-700">
                {searching ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                Research
              </Button>
            </div>
            
            <p className="text-sm text-slate-500">
              Uses Gemini AI to search MCA, LinkedIn, and public databases for company details, HR contacts, and verification status.
            </p>
            
            {webSearchResults.length > 0 && (
              <div className="border rounded-lg divide-y max-h-80 overflow-y-auto">
                {webSearchResults.map((result, idx) => (
                  <div key={idx} className="p-4 hover:bg-slate-50">
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="font-medium">{result.company_name}</div>
                        {result.industry && <div className="text-sm text-slate-600">{result.industry}</div>}
                        {result.website && <div className="text-sm text-blue-600">{result.website}</div>}
                        {result.address && <div className="text-sm text-slate-500">{result.address}</div>}
                        {result.hr_email && <div className="text-sm text-slate-500">HR: {result.hr_email}</div>}
                        <div className="text-xs text-slate-400 mt-1">Source: {result.source || 'AI Research'}</div>
                      </div>
                      <Button size="sm" onClick={() => importFromResearch(result)}>
                        <Plus className="w-4 h-4 mr-1" />Import
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowResearchDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Import Dialog */}
      <Dialog open={showBulkImportDialog} onOpenChange={setShowBulkImportDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5 text-emerald-600" />
              Bulk Import Companies
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="flex gap-2">
              <Button variant="outline" onClick={downloadTemplate}>
                <Download className="w-4 h-4 mr-2" />Download Template
              </Button>
            </div>
            <div>
              <Label>Paste CSV Data</Label>
              <Textarea
                value={importData}
                onChange={(e) => setImportData(e.target.value)}
                placeholder="company_name,industry,hr_email,hr_phone,website,contact_person,brief,status&#10;Company A,Technology,hr@a.com,..."
                rows={10}
                className="font-mono text-sm"
              />
            </div>
            <p className="text-sm text-slate-500">
              Required columns: company_name. Optional: industry, hr_email, hr_phone, website, contact_person, brief, status
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBulkImportDialog(false)}>Cancel</Button>
            <Button onClick={handleBulkImport} disabled={importing} className="bg-emerald-600 hover:bg-emerald-700">
              {importing ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
              Import
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Email Dialog */}
      <Dialog open={showBulkEmailDialog} onOpenChange={setShowBulkEmailDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MailPlus className="w-5 h-5 text-emerald-600" />
              Send Bulk Email ({selectedCompanies.length} selected)
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Subject</Label>
              <Input
                value={emailForm.subject}
                onChange={(e) => setEmailForm({...emailForm, subject: e.target.value})}
                placeholder="Email subject"
              />
            </div>
            <div>
              <Label>Message Body</Label>
              <Textarea
                value={emailForm.body}
                onChange={(e) => setEmailForm({...emailForm, body: e.target.value})}
                placeholder="Enter your email message..."
                rows={6}
              />
            </div>
            <div>
              <Label>Select Proposal Template</Label>
              <Select
                value={emailForm.selectedTemplate}
                onValueChange={(v) => setEmailForm({...emailForm, selectedTemplate: v})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Choose a proposal template (optional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">No template</SelectItem>
                  {proposalTemplates.map(t => (
                    <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-4">
              <label className="flex items-center gap-2">
                <Checkbox
                  checked={emailForm.attachBrochure}
                  onCheckedChange={(checked) => setEmailForm({...emailForm, attachBrochure: checked})}
                />
                <span className="text-sm">Attach Brochure</span>
              </label>
              <label className="flex items-center gap-2">
                <Checkbox
                  checked={emailForm.attachPricing}
                  onCheckedChange={(checked) => setEmailForm({...emailForm, attachPricing: checked})}
                />
                <span className="text-sm">Attach Pricing Sheet</span>
              </label>
            </div>
            <div className="bg-amber-50 p-3 rounded-lg text-sm">
              <p className="font-medium text-amber-700">Note:</p>
              <p className="text-amber-600">Companies will be marked as "Contacted" after sending emails.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBulkEmailDialog(false)}>Cancel</Button>
            <Button onClick={handleBulkEmail} disabled={sendingEmail} className="bg-emerald-600 hover:bg-emerald-700">
              {sendingEmail ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
              Send Emails
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Dialog - Shortened for brevity, keeping essential fields */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingCompany ? 'Edit Company' : 'Add New Company'}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-4">
            <div className="col-span-2">
              <Label>Company Name *</Label>
              <Input
                value={formData.company_name}
                onChange={(e) => setFormData({...formData, company_name: e.target.value})}
                placeholder="e.g., Tata Consultancy Services"
              />
            </div>
            <div>
              <Label>Industry</Label>
              <Input
                value={formData.industry}
                onChange={(e) => setFormData({...formData, industry: e.target.value})}
                placeholder="e.g., IT Services"
              />
            </div>
            <div>
              <Label>Status</Label>
              <Select value={formData.status} onValueChange={(v) => setFormData({...formData, status: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Website</Label>
              <Input value={formData.website} onChange={(e) => setFormData({...formData, website: e.target.value})} placeholder="https://..." />
            </div>
            <div>
              <Label>Risk Level</Label>
              <Select value={formData.risk_level} onValueChange={(v) => setFormData({...formData, risk_level: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="verified">Verified</SelectItem>
                  <SelectItem value="unverified">Unverified</SelectItem>
                  <SelectItem value="suspicious">Suspicious</SelectItem>
                  <SelectItem value="fake">Fake/Shell</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="col-span-2 border-t pt-4 mt-2">
              <h4 className="font-medium mb-3 flex items-center gap-2"><Users className="w-4 h-4" />Contact Person</h4>
            </div>
            <div>
              <Label>Name</Label>
              <Input value={formData.contact_person} onChange={(e) => setFormData({...formData, contact_person: e.target.value})} placeholder="Contact name" />
            </div>
            <div>
              <Label>Designation</Label>
              <Input value={formData.contact_person_designation} onChange={(e) => setFormData({...formData, contact_person_designation: e.target.value})} placeholder="e.g., HR Manager" />
            </div>
            <div>
              <Label>Email</Label>
              <Input type="email" value={formData.contact_person_email} onChange={(e) => setFormData({...formData, contact_person_email: e.target.value})} placeholder="contact@company.com" />
            </div>
            <div>
              <Label>Phone</Label>
              <Input value={formData.contact_person_phone} onChange={(e) => setFormData({...formData, contact_person_phone: e.target.value})} placeholder="+91..." />
            </div>
            
            <div className="col-span-2 border-t pt-4 mt-2">
              <h4 className="font-medium mb-3">HR Contact (for verification)</h4>
            </div>
            <div>
              <Label>HR Email</Label>
              <Input type="email" value={formData.hr_email} onChange={(e) => setFormData({...formData, hr_email: e.target.value})} placeholder="hr@company.com" />
            </div>
            <div>
              <Label>HR Phone</Label>
              <Input value={formData.hr_phone} onChange={(e) => setFormData({...formData, hr_phone: e.target.value})} placeholder="+91..." />
            </div>
            
            <div className="col-span-2">
              <Label>Brief / Notes</Label>
              <Textarea
                value={formData.brief}
                onChange={(e) => setFormData({...formData, brief: e.target.value})}
                placeholder="Brief description or notes about the company..."
                rows={3}
              />
            </div>
            <div className="col-span-2">
              <Label>Address</Label>
              <Textarea value={formData.address} onChange={(e) => setFormData({...formData, address: e.target.value})} placeholder="Company address" rows={2} />
            </div>
            <div>
              <Label>CIN Number</Label>
              <Input value={formData.cin_number} onChange={(e) => setFormData({...formData, cin_number: e.target.value})} />
            </div>
            <div>
              <Label>LinkedIn URL</Label>
              <Input value={formData.linkedin_url} onChange={(e) => setFormData({...formData, linkedin_url: e.target.value})} />
            </div>
            
            <div className="col-span-2 border-t pt-4 mt-2">
              <h4 className="font-medium mb-3 flex items-center gap-2"><FileText className="w-4 h-4" />Mandatory Documents for Verification</h4>
              <div className="flex flex-wrap gap-2">
                {MANDATORY_DOCS.map(doc => (
                  <label
                    key={doc.id}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-full border cursor-pointer transition-colors ${
                      formData.mandatory_docs?.includes(doc.id)
                        ? 'bg-indigo-100 border-indigo-300 text-indigo-700'
                        : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'
                    }`}
                  >
                    <Checkbox
                      checked={formData.mandatory_docs?.includes(doc.id)}
                      onCheckedChange={() => toggleMandatoryDoc(doc.id)}
                      className="hidden"
                    />
                    <span className="text-sm">{doc.label}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={handleSubmit} className="bg-indigo-600 hover:bg-indigo-700">
              {editingCompany ? 'Update' : 'Add'} Company
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
